/**
 * Shared date formatters for the candidate flows. Kept in one place so
 * the detail page preview and the per-candidate CSV export always render
 * the same date in the same shape.
 */

const MONTH_ABBREVIATIONS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Render a date as `DD-Mon-YYYY` (e.g. `28-Jul-1984`) — the format we
 * want for everywhere we show the candidate's date of birth, joining
 * date, and any other day-precision date.
 *
 * Parses the date pieces directly from the source string rather than
 * via `new Date(...)`. `new Date("YYYY-MM-DD")` is interpreted as UTC
 * midnight, which can shift to the previous day in negative-UTC
 * locales (and the trailing `T...Z` form has the same issue) — working
 * off the string parts avoids that timezone trap entirely. Falls
 * through to the raw value when the input doesn't match the expected
 * ISO shape so unexpected backend formats still render something.
 */
export const formatDayMonYear = (value: string | null | undefined): string => {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/.exec(value);
  if (!match) return value;
  const [, yyyy, mm, dd] = match;
  const monthIndex = Number(mm) - 1;
  if (monthIndex < 0 || monthIndex > 11) return value;
  return `${dd}-${MONTH_ABBREVIATIONS[monthIndex]}-${yyyy}`;
};
